import React, { useEffect, useMemo, useState } from 'react'

import { Info } from '@mui/icons-material'
import { Grid, Link, Tooltip as MUITooltip } from '@mui/material'
import {
  BooleanField,
  Button,
  EditButton,
  FunctionField,
  ImageField,
  Labeled,
  RaRecord,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useRecordContext,
  useTranslate,
} from 'react-admin'

import { Divider, Title, Tooltip } from '@mantine/core'
import { useParams } from 'react-router-dom'
import { useGetApiMonetizationPatterns } from '../../codegen/generated/monetization-patterns'
import { LXField } from '../../components/LXField'
import { LXNumberField } from '../../components/LXNumberField'
import { LXYoutubeIcon } from '../../components/LXYoutubeIcon'
import { L10nHelper } from '../../helpers/L10n.helper'
import { useUserStore } from '../../stores/useUserStore'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import styles from './YoutubeChannel.module.scss'

export const YoutubechannelShow: React.FC = props => {
  const translate = useTranslate()
  const locales = L10nHelper.getYoutubeLocales()
  const dataProvider = useDataProvider()
  const params = useParams()
  const userStore = useUserStore()

  const [youtubeChannel, setYoutubeChannel] = useState<YoutubeChannel | undefined>()

  const monetizationPatternsQuery = useGetApiMonetizationPatterns()
  const monetizationPatterns = useMemo(
    () => monetizationPatternsQuery.data?.body ?? [],
    [monetizationPatternsQuery.data?.body],
  )

  // Get artworkName for App title
  useEffect(() => {
    if (!params.id) {
      return
    }

    dataProvider
      .getOne('youtubeChannels', { id: params.id as string })
      .then(res => setYoutubeChannel(res?.data as YoutubeChannel))
  }, [dataProvider, params?.id])

  const YoutubeChannelsShowActions = () => (
    <TopToolbar
      style={{
        paddingBottom: 0,
      }}
    >
      <Tooltip
        label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
          'pages.users.permissionsList.youtubeChannels.edit',
        )}`}
        hidden={userStore.isAuthorised('youtubeChannels.edit')}
      >
        <span>
          <EditButton disabled={!userStore.isAuthorised('youtubeChannels.edit')} />
        </span>
      </Tooltip>
    </TopToolbar>
  )

  return (
    <Show
      title={`${translate('pages.youtubeChannels._title')} / ${youtubeChannel?.name ?? ''}`}
      actions={<YoutubeChannelsShowActions />}
    >
      <SimpleShowLayout>
        <Grid container direction="row" spacing={2}>
          <Grid item lg={2}>
            <ImageField source="thumbnailUrl" label={''} />
            <FunctionField
              addLabel={false}
              render={(subRecord: RaRecord | undefined) => (
                <Link href={`https://www.youtube.com/channel/${subRecord?.id}`} target="_blank">
                  <Button label={translate('pages.youtubeChannels.linkToYouTube')}>
                    <LXYoutubeIcon />
                  </Button>
                </Link>
              )}
            />
          </Grid>
          <Grid item direction="column" lg={10} spacing={2}>
            <Grid container item direction="row" lg={10} spacing={2}>
              <LXField
                source="name"
                label={translate('_generics.name')}
                elementType={TextField}
                labeled
              />
              <Grid item lg={3}>
                <Labeled label={translate('pages.youtubeChannels.subscribersCount')}>
                  <LXNumberField source="subscribersCount" format="large" />
                </Labeled>
              </Grid>
              <Grid item lg={3}>
                <Labeled label={translate('pages.youtubeChannels.videosCount')}>
                  <LXNumberField source="videosCount" format="large" />
                </Labeled>
              </Grid>
              <Grid item lg={3}>
                <Labeled label={translate('pages.youtubeChannels.viewsCount')}>
                  <LXNumberField source="viewsCount" format="large" />
                </Labeled>
              </Grid>
              <LXField
                source="cms"
                label={translate('pages.youtubeChannels.cms')}
                elementType={TextField}
                labeled
              />
              <LXField
                source="id"
                label={translate('_generics.youtubeChannelId')}
                elementType={TextField}
                size={6}
                labeled
              />
            </Grid>

            <hr style={{ width: '100%' }} />
            <Grid container item direction="row" lg={10} spacing={2}>
              <LXField
                source="isOwnedByLogicomix"
                label={translate('pages.youtubeChannels.isOwnedByLogicomix')}
                elementType={BooleanField}
                size={12}
                labeled
              />
              <LXField
                source="name"
                label={translate('pages.youtubeChannels.defaultBeneficiary')}
                elementType={TextField}
                size={6}
                wrappingFunction={el => (
                  <ReferenceField
                    source="defaultBeneficiaryId"
                    reference="beneficiaries"
                    link="show"
                  >
                    {el}
                  </ReferenceField>
                )}
                labeled
              />
              <LXField
                source="name"
                label={translate('pages.youtubeChannels.defaultProduct')}
                elementType={TextField}
                size={6}
                wrappingFunction={el => (
                  <ReferenceField source="defaultProductId" reference="products" link="show">
                    {el}
                  </ReferenceField>
                )}
                labeled
              />
            </Grid>

            <Title order={5} mt="xl">
              ClickUp
            </Title>
            <Divider />
            <Grid container item direction="row" lg={10} spacing={2}>
              <LXField
                source="clickupListId"
                label={translate('pages.youtubeChannels.clickupListId')}
                elementType={TextField}
                labeled
              />
            </Grid>

            <Title order={5} mt="xl">
              {translate('pages.youtubeChannels.tags')} &{' '}
              {translate('pages.youtubeChannels.description')}
            </Title>
            <Divider />
            <Grid container item direction="row" lg={10} spacing={2}>
              <LXField
                source="defaultUploadDescription"
                label={translate('pages.youtubeChannels.defaultUploadDescription')}
                elementType={TextField}
                size={6}
                labeled
              />
              <Grid item lg={3}>
                <Labeled label={translate('pages.youtubeChannels.defaultUploadTags')}>
                  <FunctionField
                    addLabel={false}
                    render={(subRecord: RaRecord | undefined) =>
                      (subRecord?.defaultUploadTags ?? []).join(', ')
                    }
                  />
                </Labeled>
              </Grid>
              <LXField
                source="isAutoTagUpdateEnabled"
                label={translate('pages.youtubeChannels.isAutoTagUpdateEnabled')}
                elementType={BooleanField}
                size={6}
                labeled
              />
              <YoutubeChannelShowTags />
            </Grid>

            <Title order={5} mt="xl">
              {translate('pages.automatedTreatments.monetization')}
            </Title>
            <hr style={{ width: '100%', marginTop: '5px' }} />
            <Grid container item direction="row" lg={10} spacing={2}>
              <LXField
                source="isAutoMonetizationEnabled"
                label={translate('pages.youtubeChannels.isAutoMonetizationEnabled')}
                elementType={BooleanField}
                size={6}
                labeled
                wrappingFunction={el => (
                  <Grid container item>
                    <Grid item lg={10}>
                      {el}
                    </Grid>
                    <Grid item lg={2} style={{ position: 'relative' }}>
                      <MUITooltip
                        title={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: translate('pages.youtubeChannels.autoAdsPatternTooltip'),
                            }}
                          />
                        }
                        placement="right"
                      >
                        <Info className={styles.infoShow} />
                      </MUITooltip>
                    </Grid>
                  </Grid>
                )}
              />
              <LXField
                source="autoMonetizationPolicyId"
                label={translate('pages.youtubeChannels.autoMonetizationPolicyId')}
                elementType={TextField}
                size={6}
                labeled
              />
              <LXField
                source="adsInsertionPresetId"
                label={translate('pages.youtubeChannels.adsInsertionPresetId')}
                size={6}
                labeled
              >
                <FunctionField
                  render={(subRecord: RaRecord | undefined) =>
                    monetizationPatterns.find(p => p.id === subRecord?.adsInsertionPresetId)
                      ?.name ?? '-'
                  }
                />
              </LXField>
              {
                // TODO: Enable when available on API side
                // <LXField source="isAutoInsertSubscribeEndScreenEnabled" label={translate('pages.youtubeChannels.isAutoInsertSubscribeEndScreenEnabled')} elementType={BooleanField} size={6} labeled />
              }
            </Grid>

            <Title order={5} mt="xl">
              {translate('pages.automatedTreatments.translation')}
            </Title>
            <hr style={{ width: '100%', marginTop: '5px' }} />
            <Grid container item direction="row" lg={10} spacing={2}>
              <LXField
                source="isAutoSubtitlesTranslationEnabled"
                label={translate('pages.youtubeChannels.isAutoSubtitlesTranslationEnabled')}
                elementType={BooleanField}
                size={6}
                labeled
              />
              <Grid item lg={3}>
                <Labeled label={translate('pages.youtubeChannels.subtitleMainLanguage')}>
                  <FunctionField
                    addLabel={false}
                    render={(subRecord: RaRecord | undefined) =>
                      locales.find(l => l.id === subRecord?.subtitleMainLanguage)?.name ??
                      subRecord?.subtitleMainLanguage
                    }
                  />
                </Labeled>
              </Grid>
              <Grid item lg={3}>
                <Labeled label={translate('pages.youtubeChannels.additionalSubtitlesLanguages')}>
                  <FunctionField
                    addLabel={false}
                    render={(subRecord: RaRecord | undefined) =>
                      subRecord?.subtitlesLanguages
                        .map((sl: string) => locales.find(l => l.id === sl)?.name ?? sl)
                        .join(' - ')
                    }
                  />
                </Labeled>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  )
}

const YoutubeChannelShowTags = () => {
  const record = useRecordContext()
  const translate = useTranslate()

  return (
    record?.isAutoTagUpdateEnabled && (
      <>
        <LXField
          source="autoTags"
          label={translate('pages.youtubeChannels.autoTags')}
          elementType={TextField}
          size={6}
          labeled
        />
        <LXField
          source="autoDefaultVideoLanguage"
          label={translate('pages.youtubeChannels.autoDefaultVideoLanguage')}
          elementType={TextField}
          labeled
        />
      </>
    )
  )
}
